import { addressUI as platformAddressUI } from 'platform/forms-system/src/js/web-component-patterns';
import { validateChars } from 'platform/shared/health-apps';
import content from '../locales/en/content.json';

/**
 * App-specific addressUI that extends platform addressUI with automatic defaults.
 *
 * Overrides from platform:
 * - Automatically applies character validation to street, street2, and city fields
 * - Sets default military checkbox label from content.json
 * - Extends any provided validations rather than replacing them
 *
 * @param {Object} [options={}] - Configuration options (same as platform addressUI)
 * @param {Object} [options.labels] - Custom labels; militaryCheckbox defaults to content value
 * @param {Array|Object} [options.validations] - Additional validations (array for all fields or object for field-specific)
 * @param {Array} [options.omit] - Fields to omit from address
 * @returns {UISchemaOptions} UI schema with character validation and default labels
 *
 * @example
 * // Uses default military checkbox label and character validation
 * sponsorAddress: addressUI()
 *
 * @example
 * // Override the military checkbox label
 * customAddress: addressUI({
 *   labels: { militaryCheckbox: 'Custom label' }
 * })
 *
 * @example
 * // Add additional validations
 * addressUI({ validations: [customValidation] })
 *
 * @see {@link https://va.ghe.com/software/vets-website/blob/main/src/platform/forms-system/src/js/web-component-patterns/addressPattern.jsx platform addressUI}
 */
export const addressUI = (options = {}) => {
  const labels = {
    militaryCheckbox: content['form-label--military-address'],
    ...options.labels,
  };
  const baseSchema = platformAddressUI({ ...options, labels });

  const addressCharValidation = (errors, fieldData) => {
    ['street', 'street2', 'street3', 'city'].forEach(key => {
      const value = fieldData?.[key];
      if (typeof value === 'string') {
        const fieldErrors = { addError: msg => errors[key]?.addError(msg) };
        validateChars(fieldErrors, value);
      }
    });
  };

  return {
    ...baseSchema,
    'ui:validations': [
      ...(baseSchema['ui:validations'] ?? []),
      addressCharValidation,
    ],
  };
};
